import { useEffect, useState } from 'react';

interface ViewportHeight {
  vh: number;
  realHeight: number;
  isSafari: boolean;
  isMobile: boolean;
}

export const useViewportHeight = (): ViewportHeight => {
  const [viewportHeight, setViewportHeight] = useState<ViewportHeight>({
    vh: window.innerHeight,
    realHeight: window.innerHeight,
    isSafari: false,
    isMobile: false,
  });

  useEffect(() => {
    const detectBrowser = () => {
      const userAgent = navigator.userAgent;
      const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
      const isMobile = /iPhone|iPad|iPod|Android/i.test(userAgent);

      return { isSafari, isMobile };
    };

    const updateViewportHeight = () => {
      const { isSafari, isMobile } = detectBrowser();

      // Altura padrão da viewport
      const vh = window.innerHeight;

      // Para Safari no iOS, calculamos a altura real considerando as barras de navegação
      let realHeight = vh;

      if (isSafari && isMobile) {
        // No Safari iOS, a altura real pode variar devido às barras de navegação
        // Usamos uma abordagem mais conservadora
        realHeight = Math.min(vh, window.screen.height);

        // Se estamos em orientação landscape, ajustamos para considerar a barra de navegação
        if (window.orientation === 90 || window.orientation === -90) {
          realHeight = Math.min(realHeight, window.screen.width);
        }
      }

      setViewportHeight({
        vh,
        realHeight,
        isSafari,
        isMobile,
      });
    };

    // Atualiza imediatamente
    updateViewportHeight();

    // Adiciona listeners para mudanças de orientação e redimensionamento
    window.addEventListener('resize', updateViewportHeight);
    window.addEventListener('orientationchange', () => {
      // Pequeno delay para garantir que a orientação foi aplicada
      setTimeout(updateViewportHeight, 100);
    });

    // Para Safari iOS, também escutamos mudanças na altura da viewport
    if (detectBrowser().isSafari && detectBrowser().isMobile) {
      let lastHeight = window.innerHeight;

      const checkHeightChange = () => {
        const currentHeight = window.innerHeight;
        if (currentHeight !== lastHeight) {
          lastHeight = currentHeight;
          updateViewportHeight();
        }
        requestAnimationFrame(checkHeightChange);
      };

      requestAnimationFrame(checkHeightChange);
    }

    return () => {
      window.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('orientationchange', updateViewportHeight);
    };
  }, []);

  return viewportHeight;
};
